import { Badge } from "./ui/badge";
import { formatIST, ISO_DATE_RE, parseUTCDate, formatNestedDates } from "../lib/helpers";

export function renderLastLogin(v) {
  let events = v;
  if (typeof v === "string") {
    try {
      events = JSON.parse(v);
    } catch {
      return <span className="font-data text-[11px] break-all">{v}</span>;
    }
  }
  if (!Array.isArray(events) || !events.length)
    return <span className="text-muted-foreground">—</span>;
  const toDate = (e) => {
    const t = e?.timestamp;
    if (Array.isArray(t)) return new Date(Date.UTC(t[0], (t[1] || 1) - 1, t[2] || 1, t[3] || 0, t[4] || 0, t[5] || 0));
    if (typeof t === "string") return parseUTCDate(t);
    const d = new Date(t);
    return isNaN(d) ? null : d;
  };
  const latest = events
    .map((e) => ({ date: toDate(e), device: e?.deviceModel }))
    .filter((e) => e.date)
    .sort((a, b) => b.date - a.date)
    .slice(0, 3);
  if (!latest.length) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="space-y-1" data-testid="last-login-latest-three">
      {latest.map((e, i) => (
        <div key={i} className="font-data text-[11px] whitespace-nowrap">
          {formatIST(e.date)}
          {e.device && <span className="text-muted-foreground"> · {e.device}</span>}
        </div>
      ))}
    </div>
  );
}

export function formatCell(v, key) {
  if (key === "last_login_at") return renderLastLogin(v);
  if (v === null || v === undefined || v === "")
    return <span className="text-muted-foreground">—</span>;
  if (typeof v === "boolean")
    return (
      <Badge variant={v ? "default" : "secondary"} className="font-data text-[10px]">
        {v ? "YES" : "NO"}
      </Badge>
    );
  if (typeof v === "number") {
    if (v > 1e9 && v < 2e10)
      return <span className="font-data text-xs whitespace-nowrap">{formatIST(new Date(v * 1000))}</span>;
    return <span className="font-data text-xs">{v.toLocaleString()}</span>;
  }
  if (typeof v === "object")
    return (
      <pre className="font-data text-[11px] leading-relaxed whitespace-pre-wrap max-w-lg break-words">
        {JSON.stringify(formatNestedDates(v), null, 2)}
      </pre>
    );
  if (typeof v === "string" && ISO_DATE_RE.test(v.trim())) {
    const d = parseUTCDate(v);
    if (d) return <span className="font-data text-xs whitespace-nowrap">{formatIST(d)}</span>;
  }
  return <span className="text-sm">{String(v)}</span>;
}

export function KVTable({ data, testId = "kv-table" }) {
  if (!data || typeof data !== "object" || Array.isArray(data))
    return (
      <pre className="font-data text-xs p-4 border border-border rounded-md bg-card overflow-x-auto" data-testid={testId}>
        {JSON.stringify(formatNestedDates(data), null, 2)}
      </pre>
    );
  const entries = Object.entries(data).filter(([, v]) => v !== undefined);
  if (!entries.length)
    return (
      <div className="text-sm text-muted-foreground p-4" data-testid={`${testId}-empty`}>
        No data returned.
      </div>
    );
  return (
    <div className="border border-border rounded-md bg-card overflow-hidden" data-testid={testId}>
      <table className="w-full">
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={k} className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors">
              <td className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground w-1/3 align-top font-medium">
                {k.replace(/_/g, " ")}
              </td>
              <td className="px-4 py-2.5">{formatCell(v, k)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DynamicTable({ rows, testId = "data-table", onRowClick, renderActions, maxCols = 9, empty = "No records found." }) {
  if (!rows?.length)
    return (
      <div className="p-8 text-sm text-muted-foreground border border-dashed border-border rounded-md" data-testid={`${testId}-empty`}>
        {empty}
      </div>
    );
  const cols = [...new Set(rows.slice(0, 10).flatMap((r) => Object.keys(r || {})))].slice(0, maxCols);
  return (
    <div className="overflow-x-auto border border-border rounded-md bg-card" data-testid={testId}>
      <table className="w-full">
        <thead>
          <tr className="border-b border-border bg-muted/50">
            {cols.map((c) => (
              <th key={c} className="text-left px-4 py-3 font-medium text-[11px] uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                {c.replace(/_/g, " ")}
              </th>
            ))}
            {renderActions && <th className="px-4 py-3 w-10" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={i}
              data-testid={`${testId}-row-${i}`}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
              className={`border-b border-border last:border-0 transition-colors ${
                onRowClick ? "cursor-pointer hover:bg-accent/60" : "hover:bg-muted/40"
              }`}
            >
              {cols.map((c) => (
                <td key={c} className="px-4 py-3 align-top">
                  {formatCell(r?.[c], c)}
                </td>
              ))}
              {renderActions && <td className="px-4 py-3 align-top">{renderActions(r)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
